import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import img from "../components/all_bg.jpg";
import { useNavigate } from "react-router-dom";

const sections = [
  { key: "personal_information", title: "Personal Information" },
  { key: "research_profile", title: "Research Profile" },
  { key: "education", title: "Education" },
  { key: "academic_experience", title: "Academic Experience" },
  {
    key: "academic_administrative_role",
    title: "Academic Administrative Role",
  },
  { key: "academic_event_organized", title: "Academic Events Organized" },
  { key: "case_publications", title: "Case Publications" },
  { key: "industry_experience", title: "Industry Experience" },
  { key: "industry_report", title: "Industry Reports" },
  { key: "professional_certification", title: "Professional Certifications" },
  {
    key: "professional_certificate_development",
    title: "Professional Certificate Development",
  },
  {
    key: "organizational_membership",
    title: "Organizational/Professional Memberships",
  },
  { key: "book", title: "Books" },
  { key: "book_chapters", title: "Book Chapters" },
  { key: "book_review_international", title: "Book Review International" },
  { key: "book_review_local", title: "Book Review Local" },
  { key: "text_book", title: "Textbooks" },
  { key: "academic_journal_articles", title: "Academic Journal Articles" },
  { key: "trade_journal", title: "Trade Journal Publications" },
  {
    key: "conference_proceeding",
    title: "Conference Proceedings/Presentations",
  },
  { key: "conference_session_chair", title: "Keynote/Invited/Session Chairs" },
  {
    key: "intellectual_contributions",
    title: "Portfolio of Intellectual Contributions",
  },
  { key: "intellectual_property", title: "Intellectual Property" },
  {
    key: "international_academic_contribution",
    title: "International Academic Contribution",
  },
  {
    key: "new_degree_course_development",
    title: "New Degree/Course Developments",
  },
  { key: "new_program_development", title: "New Program Developments" },

  { key: "course_coordination", title: "Course Coordination" },
  { key: "op_ed", title: "Op-eds" },
  {
    key: "academic_policy_contribution",
    title: "Academic Policy Contribution",
  },
  { key: "policy_briefs", title: "Policy Briefs" },
  { key: "policy_advisory_role", title: "Policy Advisory Roles" },
  { key: "consultation_services", title: "Consultation Services" },
  { key: "contribution_to_media", title: "Contribution to Media" },
  { key: "editorial_service", title: "Editorial Services" },
  { key: "research_supervision", title: "Graduate Research Supervision" },
  { key: "research_grant", title: "Graduate Research Grants Received" },
  {
    key: "research_grant_proposal_reviewed",
    title: "Research Grant Proposals Reviewed",
  },
  { key: "social_service", title: "Community/Social Service Activities" },
  {
    key: "community_outreach",
    title: "Community Outreach / Engagement Service Activities",
  },
  { key: "event_workshop", title: "Organized Events/Workshops/Field Trips" },
  { key: "committee_service", title: "Committee Services" },
  { key: "awards", title: "Awards" },
  { key: "speech_delivered", title: "Speeches Delivered" },
  { key: "public_interest_engagement", title: "Public Interest Engagement" },
  { key: "brand_membership", title: "Brand Memberships" },
  { key: "business_ownership", title: "Business Ownerships" },
  { key: "training_delivered", title: "Trainings Delivered" },
];

const BACKEND_URL = "https://api.bbsfacultyprofile.com";

/*
 * These endpoints may return the logged-in user's rows without a "pin"
 * column. For them, the pin filter is applied only when the rows
 * actually carry a pin; otherwise all returned rows are kept.
 */
const OPTIONAL_PIN_FILTER = [
  "book_review_international",
  "book_review_local",
];

export default function FacultyPage() {
  const { id } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const handleDownloadCV = async () => {
    if (!token) {
      toast.warning("You need to login first!");
      return;
    }

    const toastId = toast.info(
      <div>
        Generate and download CV?{" "}
        <button
          onClick={async () => {
            try {
              const isAdmin = localStorage.getItem("role") === "admin";
              const url = isAdmin
                ? `${BACKEND_URL}/api/admin/faculty/${id}/download-cv`
                : `${BACKEND_URL}/api/download-cv`;

              const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
              });

              const disposition = response.headers["content-disposition"];
              const match =
                disposition && disposition.match(/filename="?(.+)"?/);
              const filename = match
                ? decodeURIComponent(match[1])
                : isAdmin
                ? `faculty_${id}_cv.pdf`
                : `faculty_cv.pdf`;

              const blobUrl = window.URL.createObjectURL(
                new Blob([response.data])
              );
              const a = document.createElement("a");
              a.href = blobUrl;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(blobUrl);

              toast.update(toastId, {
                render: "Downloading CV...",
                type: "success",
                autoClose: 3000,
                closeButton: true,
              });
            } catch (err) {
              console.error(err);
              toast.update(toastId, {
                render: "Failed to download CV. Check token or permissions.",
                type: "error",
                autoClose: 3000,
                closeButton: true,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = {};
        for (const { key } of sections) {
          try {
            const res = await axios.get(`${BACKEND_URL}/api/${key}?pin=${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            // --- LOG RESPONSE ---
            console.log(key, "API response:", res.data);
            if (key === "personal_information") {
              const arr = Array.isArray(res.data) ? res.data : [res.data];

              results[key] = arr.map((row) => {
                const salutation = row.salutation?.trim() || "";
                const name = row.name?.trim() || "";

                return {
                  ...row,
                  // merge salutation + name
                  name:
                    salutation || name
                      ? `${salutation} ${name}`.trim()
                      : row.name,
                  // remove salutation column
                  salutation: undefined,
                };
              });
            } else if (OPTIONAL_PIN_FILTER.includes(key)) {
              // Supports both [ {...} ] and { data: [ {...} ] }
              const list = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res.data?.data)
                ? res.data.data
                : [];

              const hasPin = list.some(
                (f) => f.pin !== undefined && f.pin !== null
              );

              results[key] = hasPin
                ? list.filter((f) => String(f.pin) === String(id))
                : list;
            } else {
              results[key] = Array.isArray(res.data)
                ? res.data.filter((f) => String(f.pin) === String(id))
                : [];
            }
          } catch (err) {
            console.error(key, "fetch failed:", err);
            results[key] = [];
          }
        }

        // LOG AFTER BUILDING RESULTS
        console.log("Final results object:", results);

        setData(results);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load faculty data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token]);

  if (loading)
    return (
      <p style={{ textAlign: "center", padding: "50px" }}>
        Loading faculty data...
      </p>
    );

  const facultyInfo = data.personal_information?.[0];
  console.log("facultyInfo object:", facultyInfo);

  let facultyName = "N/A";

  if (facultyInfo) {
    console.log("facultyInfo.name:", facultyInfo.name);
    console.log("facultyInfo.salutation:", facultyInfo.salutation);

    const salutation = facultyInfo.salutation?.trim() || "";
    const name = facultyInfo.name?.trim() || "";

    if (salutation || name) {
      facultyName = `${salutation} ${name}`.trim();
    } else {
      facultyName = "(Profile not completed)";
    }
  }

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date)) return value;
    return `${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}/${date.getFullYear()}`;
  };

  localStorage.setItem("facultyData", JSON.stringify(data));
  localStorage.setItem("aacsbPin", id);

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center top",
        backgroundAttachment: "fixed",
        minHeight: "100vh",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />

      <Link
        to="/admin_dashboard"
        style={{
          textDecoration: "underline",
          color: "#1e3a8a",
          fontWeight: "bold",
        }}
      >
        ← Back to Dashboard
      </Link>

      <p>
        <h2
          style={{
            marginTop: "15px",
            fontSize: "1.5rem",
            background: "#1d4786a1",
            color: "#ffffffff",
            padding: "6px 10px",
            borderRadius: "5px",
            display: "inline-block",
            fontFamily: "times new roman",
          }}
        >
          {facultyName} (PIN: {id})
        </h2>
      </p>

      <button
        onClick={handleDownloadCV}
        style={{
          padding: "10px 15px",
          background: "#3b82f6",
          color: "#fff",
          borderRadius: "6px",
          border: "none",
          cursor: "pointer",
          marginLeft: "15px",
          fontWeight: "bold",
        }}
      >
        ⬇ Download CV
      </button>
      <button
        onClick={() => navigate(`/aacsb/${id}`)}
        style={{
          padding: "10px 15px",
          background: "#10b981",
          color: "#fff",
          borderRadius: "6px",
          border: "none",
          cursor: "pointer",
          marginLeft: "15px",
          fontWeight: "bold",
        }}
      >
        View AACSB Metrics
      </button>

      {sections.map((section) => {
        const rows = data[section.key];
        if (!rows || rows.length === 0) return null;

        const columns = Object.keys(rows[0]).filter(
          (col) => col !== "id" && col !== "pin"
        );

        return (
          <div key={section.key} style={{ marginTop: "20px" }}>
            <h3
              style={{
                background: "#1d4786a1",
                color: "#fff",
                padding: "8px 12px",
                borderRadius: "5px",
              }}
            >
              {section.title}
            </h3>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col}
                        style={{
                          border: "1px solid #000",
                          padding: "8px",
                          background: "#2c2d8370",
                          textTransform: "capitalize",
                        }}
                      >
                        {col.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id || Math.random()}>
                      {columns.map((col) => {
                        const value = row[col];
                        const stringValue =
                          value != null ? String(value).trim() : "";

                        // review_year is a plain year, so it is shown as-is
                        const isDateField =
                          col !== "review_year" &&
                          (col.toLowerCase().includes("date") ||
                            col.toLowerCase().includes("year") ||
                            /^\d{4}-\d{2}-\d{2}/.test(stringValue));

                        return (
                          <td
                            key={col}
                            style={{ border: "1px solid #000", padding: "8px" }}
                          >
                            {isDateField
                              ? formatDate(stringValue)
                              : stringValue || "N/A"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
